/**
 * Storage Health Dashboard (Feb 2026).
 *
 * Surfaces the disk-vs-GridFS state so the admin can spot any
 * disk-only orphans BEFORE the next pod redeploy wipes them
 * permanently. The companion endpoints are admin-only:
 *
 *   GET  /api/admin/storage/health
 *   POST /api/admin/storage/migrate
 *
 * The page renders three cards:
 *   • GridFS inventory  — count + size + newest upload.
 *   • Disk inventory    — count + size + breakdown per top-level folder.
 *   • Mismatch table    — disk-only orphans (red), GridFS-only (yellow).
 *
 * Action: a single "Migrate Now" button replays the upload-walker
 * synchronously. Refreshes the health card on completion.
 */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import { HardDrive, Database, AlertTriangle, CheckCircle2, RefreshCw, PlayCircle } from 'lucide-react';

export default function StorageHealth() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/admin/storage/health');
      setHealth(r.data);
    } catch (e) {
      toast.error(`فشل تحميل حالة التخزين: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runMigration = async () => {
    if (!window.confirm('سيتم نقل جميع الملفات الموجودة على القرص فقط إلى GridFS. هل تريد المتابعة؟')) return;
    setMigrating(true);
    try {
      const r = await axiosInstance.post('/admin/storage/migrate');
      toast.success(r.data.message_ar || `migrated=${r.data.migrated}`);
      await load();
    } catch (e) {
      toast.error(`فشل الترقية: ${e.response?.data?.detail || e.message}`);
    } finally {
      setMigrating(false);
    }
  };

  const fmtBytes = (mb) => mb < 1 ? `${(mb * 1024).toFixed(1)} KB` : `${mb.toFixed(2)} MB`;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="storage-health-page">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">حالة التخزين / Storage Health</h1>
          <p className="text-sm text-slate-500 mt-1">
            عرض الملفات في GridFS (دائم) مقابل القرص (مؤقت). الملفات الموجودة فقط على القرص معرّضة للضياع عند إعادة تشغيل الخادم.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            data-testid="reload-health-btn"
            onClick={load}
            disabled={loading}
            className="px-3 py-2 text-sm border border-slate-300 rounded hover:bg-slate-50 flex items-center gap-1.5"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> تحديث
          </button>
          <button
            data-testid="run-migration-btn"
            onClick={runMigration}
            disabled={migrating || !health}
            className="px-4 py-2 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded flex items-center gap-1.5 disabled:opacity-50"
          >
            <PlayCircle size={14} /> {migrating ? 'جارٍ الترقية…' : 'ترقية الآن'}
          </button>
        </div>
      </header>

      {!health && (
        <div className="text-slate-500 text-sm">جارٍ التحميل…</div>
      )}

      {health && (
        <>
          {/* Top status banner */}
          <div
            data-testid="health-banner"
            className={`mb-6 p-4 rounded-lg border-2 ${health.is_healthy
              ? 'bg-emerald-50 border-emerald-300'
              : 'bg-rose-50 border-rose-300'}`}
          >
            <div className="flex items-center gap-3">
              {health.is_healthy
                ? <CheckCircle2 className="text-emerald-600" size={28} />
                : <AlertTriangle className="text-rose-600" size={28} />}
              <div>
                <div className="font-bold text-lg">
                  {health.is_healthy
                    ? '✅ النظام سليم — جميع الملفات محفوظة في GridFS'
                    : `⚠ تحذير — ${health.orphans_disk_only.length} ملف على القرص فقط (في خطر)`}
                </div>
                <div className="text-sm text-slate-600">
                  {health.is_healthy
                    ? 'لا توجد ملفات معرّضة للضياع عند إعادة تشغيل الخادم.'
                    : 'هذه الملفات ستختفي عند إعادة Deploy. اضغط "ترقية الآن" لنقلها فوراً إلى GridFS.'}
                </div>
              </div>
            </div>
          </div>

          {/* Inventory cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div data-testid="gridfs-card" className="bg-white rounded-lg border border-slate-200 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Database className="text-emerald-600" size={20} />
                <h3 className="font-bold text-slate-800">GridFS — التخزين الدائم</h3>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-slate-500">عدد الملفات</div>
                  <div className="text-2xl font-bold text-emerald-700" data-testid="gridfs-count">{health.gridfs.count}</div>
                </div>
                <div>
                  <div className="text-slate-500">الحجم الإجمالي</div>
                  <div className="text-2xl font-bold text-emerald-700">{fmtBytes(health.gridfs.size_mb)}</div>
                </div>
              </div>
              {health.gridfs.newest_upload_at && (
                <div className="mt-3 text-xs text-slate-500">
                  آخر رفع: <span className="font-mono">{new Date(health.gridfs.newest_upload_at).toLocaleString('ar-SA')}</span>
                </div>
              )}
            </div>

            <div data-testid="disk-card" className="bg-white rounded-lg border border-slate-200 p-5">
              <div className="flex items-center gap-2 mb-3">
                <HardDrive className="text-amber-600" size={20} />
                <h3 className="font-bold text-slate-800">القرص — التخزين المؤقت</h3>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-slate-500">عدد الملفات</div>
                  <div className="text-2xl font-bold text-amber-700" data-testid="disk-count">{health.disk.count}</div>
                </div>
                <div>
                  <div className="text-slate-500">الحجم الإجمالي</div>
                  <div className="text-2xl font-bold text-amber-700">{fmtBytes(health.disk.size_mb)}</div>
                </div>
              </div>
              {Object.keys(health.disk.by_subfolder || {}).length > 0 && (
                <div className="mt-3 text-xs text-slate-500">
                  حسب المجلد:
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {Object.entries(health.disk.by_subfolder).map(([folder, count]) => (
                      <span key={folder} className="px-1.5 py-0.5 bg-slate-100 rounded font-mono">
                        {folder}: <strong>{count}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Orphan tables */}
          {health.orphans_disk_only.length > 0 && (
            <div data-testid="orphans-disk-only-section" className="mb-6 bg-white rounded-lg border-2 border-rose-300 p-5">
              <h3 className="font-bold text-rose-800 flex items-center gap-2 mb-3">
                <AlertTriangle size={18} /> ملفات على القرص فقط ({health.orphans_disk_only.length})
                <span className="text-xs font-normal text-slate-500">(معرّضة للضياع عند إعادة Deploy)</span>
              </h3>
              <div className="overflow-x-auto max-h-72 overflow-y-auto">
                <table className="w-full text-xs font-mono">
                  <thead className="bg-rose-50 sticky top-0">
                    <tr>
                      <th className="text-left p-1.5">الملف</th>
                      <th className="text-left p-1.5">الحجم</th>
                      <th className="text-left p-1.5">آخر تعديل</th>
                    </tr>
                  </thead>
                  <tbody>
                    {health.orphans_disk_only.map((o, i) => (
                      <tr key={i} className="border-t border-rose-100">
                        <td className="p-1.5 truncate max-w-md" title={o.filename}>{o.filename}</td>
                        <td className="p-1.5">{(o.size / 1024).toFixed(1)} KB</td>
                        <td className="p-1.5 text-slate-500">{new Date(o.mtime * 1000).toLocaleString('ar-SA')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {health.orphans_gridfs_only.length > 0 && (
            <details className="mb-6 bg-white rounded-lg border border-slate-200 p-5">
              <summary className="font-bold text-slate-700 cursor-pointer">
                ملفات في GridFS فقط ({health.orphans_gridfs_only.length})
                <span className="text-xs font-normal text-slate-500"> — آمنة، لا تحتاج إجراء</span>
              </summary>
              <div className="mt-3 overflow-x-auto max-h-72 overflow-y-auto">
                <table className="w-full text-xs font-mono">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-1.5">الملف</th>
                      <th className="text-left p-1.5">الحجم</th>
                      <th className="text-left p-1.5">تاريخ الرفع</th>
                    </tr>
                  </thead>
                  <tbody>
                    {health.orphans_gridfs_only.map((o, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="p-1.5 truncate max-w-md" title={o.filename}>{o.filename}</td>
                        <td className="p-1.5">{o.length ? `${(o.length / 1024).toFixed(1)} KB` : '—'}</td>
                        <td className="p-1.5 text-slate-500">{o.uploadDate ? new Date(o.uploadDate).toLocaleString('ar-SA') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}
